import { useContext, useLayoutEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import cx from 'classnames';

import { PanelSection } from '../SidePanel';
import { useNotesContext } from '../../contexts/NotesContext';
import { PANEL_INNER_HEIGHT } from './constants';
import { FieldsPreviewContext, SectionHeader } from './components';
import { useChunksByHeights } from './utils/content';
import { MapContext } from '../../contexts/MapContext';

const NOTE_CLASS_NAME = 'preview-page-notes';

const useHiddenAnchorIds = () => {
  const {
    fields: { fieldSections },
    layers: { layers },
  } = useContext(MapContext);

  return useMemo(() => {
    const hiddenSectionIds = fieldSections
      .filter((s) => !s.shown)
      .map((s) => s.id);

    const hiddenLayers = Object.values(layers).filter((l) => !l.shown);

    const hiddenLayerIds = hiddenLayers.map((l) => l.layerId);

    const hiddenLayerFeatureIds = hiddenLayers.flatMap((l) =>
      Object.values(l.layerFeatureRecords).map((lf) => lf.id),
    );

    return new Set([
      ...hiddenSectionIds,
      ...hiddenLayerIds,
      ...hiddenLayerFeatureIds,
    ]);
  }, [fieldSections, layers]);
};

const NoteSectionHeader = ({ anchorId }) => {
  const { anchorDataById } = useNotesContext();
  const { anchorName } = anchorDataById[anchorId] ?? {};

  return (
    <div className="pt-2 pb-2 text-truncate font-medium font-weight-bold">
      {anchorName}
    </div>
  );
};

const Note = ({ note, header = null }) => {
  return (
    <div className={cx(NOTE_CLASS_NAME, 'pb-1')}>
      {header}
      <div className="text-truncate font-medium pb-1">{note.title}</div>
      <div className="text-break font-small pb-2">{note.body}</div>
    </div>
  );
};

export const useNotesInfo = () => {
  const { isPrintMode, panelVisibility, togglePanelVisibility } = useContext(
    FieldsPreviewContext,
  );

  const { notesByAnchorId, anchorDataById } = useNotesContext();
  const hiddenAnchorIds = useHiddenAnchorIds();

  // Remove notes from hidden anchors, then sort by anchor type, then anchor name, then note title.
  // `notes` is an array of [anchorId, notesForAnchor] pairs.
  const sortedNotesByAnchor = useMemo(() => {
    const byAnchorType = ([, [{ anchorId }]]) =>
      anchorDataById[anchorId]?.anchorType;
    const byAnchorName = ([, [{ anchorId }]]) =>
      anchorDataById[anchorId]?.anchorName;

    // remove notes w/ anchors that are hidden
    const filteredNotesByAnchor = Object.entries(notesByAnchorId).filter(
      ([anchorId]) => !hiddenAnchorIds.has(anchorId),
    );

    // within each anchor, sort by note title
    const notesSortedWithinAnchors = filteredNotesByAnchor.map(
      ([anchorId, notesForAnchor]) => [
        anchorId,
        _.sortBy(notesForAnchor, 'title'),
      ],
    );

    // sort anchors by type and then name
    return _.sortBy(notesSortedWithinAnchors, [byAnchorType, byAnchorName]);
  }, [notesByAnchorId, hiddenAnchorIds, anchorDataById]);

  const [heights, setHeights] = useState([]);
  useLayoutEffect(() => {
    const newHeights = Array.from(
      document.getElementsByClassName(NOTE_CLASS_NAME),
    ).map((el) => el.clientHeight);

    setHeights(newHeights);
  }, [sortedNotesByAnchor]);

  const content = useMemo(
    () =>
      sortedNotesByAnchor.flatMap(([anchorId, notes]) =>
        notes.map((note, i) => (
          <Note
            key={note.id}
            note={note}
            header={
              i === 0 && (
                <NoteSectionHeader anchorId={anchorId} key={anchorId} />
              )
            }
          />
        )),
      ),
    [sortedNotesByAnchor],
  );
  const chunkedContent = useChunksByHeights(
    content,
    heights,
    PANEL_INNER_HEIGHT,
  );

  const header = (
    <SectionHeader
      title="Notes"
      onClick={() => togglePanelVisibility('notes')}
    />
  );

  if (!panelVisibility.notes) {
    if (isPrintMode) return [];

    return [
      <PanelSection className="opacity-50" key="notes-hidden">
        {header}
      </PanelSection>,
    ];
  }

  return chunkedContent.map((chunk, i) => (
    <PanelSection
      $hideBottomBorder={i !== chunkedContent.length - 1}
      key={chunk.map(({ key }) => key).join('-')}
    >
      {i === 0 && header}
      {chunk}
    </PanelSection>
  ));
};
